import { ApplicationRef, ComponentRef, inject, Injectable, Injector } from '@angular/core';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { defer, exhaustMap, from, fromEvent, isObservable, Observable, of, take, takeUntil, timer } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { BrowserDialogContentComponent } from './internal/browser-dialog-content.component';
import { DialogService } from '../../dialog.service';
import {
  isNewWindowDialogOptions,
  OpenDialogOptions,
  OverlayDialogOptions
} from '@shared/ui/kit/dialog/open-dialog-options';
import { ReturnDialogData } from '@shared/ui/kit/dialog/return-dialog-data';
import { isAfterDialogOpen, isBeforeDialogClose } from '@shared/ui/kit/dialog/after-dialog-open';
import { LOOP_DYNAMIC_COMPONENTS } from '@shared/modules/loop/components/loop-dynamic-components';
import { everyTrue } from '@shared/utils/rxjs/every-true';
import { KeyboardShortcutsListenerService } from '@dta/ui/services/keyboard-shortcuts-listener/keyboard-shortcuts-listener.service';
import { KeyboardActionModeType, KeyboardActionType } from '@dta/shared/models/keyboard-shortcut.model';
import { ComponentPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { DynamicDialogCommentComponent } from '@shared/modules/comments/components/dynamic-dialog/dynamic-dialog-comment/dynamic-dialog-comment.component';
import { writeComponentRefChanges } from '@shared/shared/utils/common/write-component-ref-changes';
import { isStandaloneComponent } from '@shared/utils/common/components/component-definition';
import {
  BROWSER_EXTERNAL_WINDOW_OPTIONS,
  BrowserExternalWindowOptions
} from '@shared/ui/kit/dialog/common/injection-tokens/browser-external-window-options';
import { IS_EXTERNAL_WINDOW } from '@shared/ui/kit/dialog/common/injection-tokens/is-external-window';

@Injectable()
export class BrowserDialogService extends DialogService {
  private readonly dialogModuleApi: Dialog = inject(Dialog);
  private readonly overlay: Overlay = inject(Overlay);
  protected readonly keyboardShortcutsListenerService: KeyboardShortcutsListenerService = inject(
    KeyboardShortcutsListenerService
  );
  private readonly applicationRef: ApplicationRef = inject(ApplicationRef);
  private readonly injector: Injector = inject(Injector);

  private externalWindowCloseFn: (() => void) | null = null;

  private openInExternalWindow$<C>(options: OpenDialogOptions<C>): Observable<ReturnDialogData<C>> {
    const windowInstance = window.open(
      'about:blank',
      '',
      `width=${options.width ?? 800}px,height=${options.height ?? 800}px`
    );

    if (!windowInstance) {
      return of(undefined);
    }

    this.externalWindowCloseFn?.();
    this.externalWindowCloseFn = () => {
      windowInstance.close();
    };

    const outlet = new DomPortalOutlet(windowInstance.document.body, this.applicationRef, this.injector);

    // Copy stylesheet links and component styles already present in the parent window
    document.querySelectorAll('link, style').forEach(htmlElement => {
      windowInstance.document.head.appendChild(htmlElement.cloneNode(true));
    });

    // Angular inserts component styles into the parent document's <head> lazily, the first time each
    // component renders (including ones rendered later, e.g. after an async API call resolves), and always
    // into the parent window rather than this popup. Keep mirroring new <style>/<link> elements as they appear.
    const headObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node instanceof HTMLStyleElement || node instanceof HTMLLinkElement) {
            windowInstance.document.head.appendChild(node.cloneNode(true));
          }
        });
      });
    });
    headObserver.observe(document.head, { childList: true });

    options.title$.pipe(take(1)).subscribe(title => {
      windowInstance.document.title = title;
    });

    // Attach portal
    const portal = new ComponentPortal(
      DynamicDialogCommentComponent,
      null,
      Injector.create({
        providers: [
          {
            provide: BROWSER_EXTERNAL_WINDOW_OPTIONS,
            useValue: {
              closeFn: () => {
                if (!windowInstance.closed) {
                  windowInstance.close();
                  window.focus();
                }
              }
            } satisfies BrowserExternalWindowOptions
          },
          {
            provide: IS_EXTERNAL_WINDOW,
            useValue: true
          }
        ],
        parent: this.injector
      })
    );

    const componentRef: ComponentRef<DynamicDialogCommentComponent> = outlet.attach(portal);
    writeComponentRefChanges(componentRef, options.componentParams);

    // Cleanup when window closes
    windowInstance.addEventListener('beforeunload', () => {
      headObserver.disconnect();
      outlet.detach();
      componentRef.destroy();
    });

    return of({
      afterClose$: fromEvent(windowInstance, 'beforeunload'),
      close: () => windowInstance.close(),
      componentInstance: componentRef.instance as C
    } satisfies ReturnDialogData<C>);
  }

  openDialog<C>(options: OpenDialogOptions<C>): Observable<ReturnDialogData<C>> {
    this.externalWindowCloseFn?.();

    return defer(() => {
      return this.getOverlayOptions(options).pipe(
        switchMap(options => {
          if (options.shouldOpenInExternalWindowWeb) {
            return this.openInExternalWindow$(options);
          }

          const positionStrategy =
            options?.flexibleConnectedPositionStrategy ??
            this.overlay.position().global().centerHorizontally().centerVertically();
          options.isClosable = options.isClosable === undefined ? true : options.isClosable;
          // We must use defer so the dialog isn't presented until the returned observable is subscribed
          const dialogRef = this.dialogModuleApi.open(BrowserDialogContentComponent, {
            width: options.isFullScreen ? '100%' : (options.width ?? '520px'),
            panelClass: ['loop-dialog-panel', options.animationDirection],
            backdropClass: 'loop-dialog-backdrop',
            disableClose: options.isCloseAllowed$ !== undefined ? true : !options.isClosable,
            height: options.isFullScreen ? '100%' : (options.height ?? undefined),
            positionStrategy
          });

          const closeFn = (): void =>
            this.triggerDialogClose({
              closeFn: () => {
                dialogRef.close();
                this.keyboardShortcutsListenerService.setMode(KeyboardActionModeType.NAVIGATION);
              },
              isCloseAllowed$: options.isCloseAllowed$ ?? of(true),
              dialogClosed$: dialogRef.closed
            });

          if (dialogRef.componentInstance) {
            (dialogRef.componentInstance.options as OpenDialogOptions<C>) = options;
            dialogRef.componentInstance.dialogRef = dialogRef;
            dialogRef.componentInstance.close = () => closeFn();
          }

          return dialogRef.componentInstance!.componentRef.pipe(
            map(componentRef => {
              const callAfterDialogOpen = isAfterDialogOpen(componentRef.instance);
              if (callAfterDialogOpen) {
                // We have to wait for animation in browser-dialog-content.scss to complete after executing the function
                timer(300)
                  .pipe(take(1))
                  .subscribe(() => {
                    if (callAfterDialogOpen && componentRef.instance) {
                      componentRef.instance.afterDialogOpen();
                    }
                  });
              }

              const callBeforeDialogClose = isBeforeDialogClose(componentRef.instance);
              if (callBeforeDialogClose) {
                const isCloseAllowed$ = everyTrue(
                  options.isCloseAllowed$ ?? of(true),
                  componentRef.instance.beforeDialogClose$()
                );

                this.observeNativeDialogCloseEvents(dialogRef, isCloseAllowed$);

                const closeFn = (): void =>
                  this.triggerDialogClose({
                    closeFn: () => dialogRef.close(),
                    isCloseAllowed$: isCloseAllowed$,
                    dialogClosed$: dialogRef.closed
                  });

                if (dialogRef.componentInstance) {
                  dialogRef.componentInstance.close = () => closeFn();
                }
              } else {
                this.observeNativeDialogCloseEvents(dialogRef, options.isCloseAllowed$);
              }

              const returnData: ReturnDialogData<C> = {
                afterClose$: dialogRef.closed,
                componentInstance: componentRef.instance as C,
                close: () => closeFn()
              };
              return returnData;
            })
          );
        })
      );
    });
  }

  private observeNativeDialogCloseEvents(
    dialogRef: DialogRef<any, any>,
    isCloseAllowed$: Observable<boolean> | undefined
  ): void {
    if (!isObservable(isCloseAllowed$)) {
      return;
    }

    const observeDialogCloseTriggerToClose = (closeTrigger$: Observable<any>): void => {
      closeTrigger$
        .pipe(
          exhaustMap(() => isCloseAllowed$.pipe(take(1))),
          filter(isConfirmed => !!isConfirmed),
          takeUntil(dialogRef.closed)
        )
        .subscribe(() => {
          dialogRef.close();
        });
    };

    observeDialogCloseTriggerToClose(dialogRef.backdropClick.pipe(tap(a => console.log(a))));

    observeDialogCloseTriggerToClose(dialogRef.keydownEvents.pipe(filter(event => event.code === 'Escape')));
  }

  private getOverlayOptions<C>(options: OpenDialogOptions<C>): Observable<OverlayDialogOptions<C>> {
    if (!isNewWindowDialogOptions(options)) {
      return of(options);
    }

    const component = LOOP_DYNAMIC_COMPONENTS[options.componentId];
    if (!component.component) {
      return of(null);
    }

    return from(component.component()).pipe(
      map(resolvedComponent => {
        return {
          ...options,
          isClosable: (options as OverlayDialogOptions<any>).isClosable,
          isCloseAllowed$: of(true),
          componentData: {
            component: isStandaloneComponent(resolvedComponent)
              ? resolvedComponent.standaloneComponent
              : resolvedComponent.component,
            module: isStandaloneComponent(resolvedComponent) ? undefined : resolvedComponent.module
          },
          componentParams: options.componentParams as any
        } as OverlayDialogOptions<C>;
      })
    );
  }
}
